import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { useAuth } from "@/context/Auth/AuthProvider";
import {
  INTEGRATION_FIELD_NAMES,
  IntegrationErrors,
  IntegrationObjectState
} from "@/Pages/Integrations/types";
import {
  IntegrationsErrorDialogSteps,
  ROUTES,
  useMediaQueries
} from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useIntegrationsTableStore } from "@/stores/useTableStore";

import ConfirmDeactivateResource from "../../reusableDialogs/ConfirmDeactivateResource/ConfirmDeactivateResource";
import IntegrationDialogContent from "../IntegrationDialogContent";

interface AddIntegrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddIntegrationDialog = ({
  isOpen,
  onClose
}: AddIntegrationDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { user } = useAuth();
  const { postData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleCommonError } = useResponse();

  const showError = useIntegrationsDialogStore(state => state.showError);
  const goBackToTypeSelect = useIntegrationsDialogStore(
    state => state.goBackToTypeSelect
  );
  const integrationsParams = useIntegrationsTableStore(state => state.params);

  const initialIntegrationObject: IntegrationObjectState = {
    [INTEGRATION_FIELD_NAMES.SelectedCompany]: user?.selectedCompany || null,
    [INTEGRATION_FIELD_NAMES.Name]: "",
    [INTEGRATION_FIELD_NAMES.FileFormat]: null,
    [INTEGRATION_FIELD_NAMES.VehicleFolderNaming]: null,
    [INTEGRATION_FIELD_NAMES.DriverFolderNaming]: null,
    [INTEGRATION_FIELD_NAMES.Host]: "",
    [INTEGRATION_FIELD_NAMES.Port]: "",
    [INTEGRATION_FIELD_NAMES.RemotePath]: "",
    [INTEGRATION_FIELD_NAMES.Username]: "",
    [INTEGRATION_FIELD_NAMES.Password]: "",
    [INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: false,
    [INTEGRATION_FIELD_NAMES.SyncHistoricalFiles]: false,
    [INTEGRATION_FIELD_NAMES.Enabled]: true
  };

  const initialErrors: IntegrationErrors = {
    [INTEGRATION_FIELD_NAMES.Name]: "",
    [INTEGRATION_FIELD_NAMES.Host]: "",
    [INTEGRATION_FIELD_NAMES.Port]: "",
    [INTEGRATION_FIELD_NAMES.RemotePath]: "",
    [INTEGRATION_FIELD_NAMES.Username]: "",
    [INTEGRATION_FIELD_NAMES.Password]: ""
  };

  const [integrationsObject, setIntegrationsObject] =
    useState<IntegrationObjectState>(initialIntegrationObject);
  const [errors, setErrors] = useState<IntegrationErrors>(initialErrors);
  const [isLoading, setIsLoading] = useState(false);

  const [hasUpdatedEnable, setHasUpdatedEnable] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);

  const resetAllValues = () => {
    setIntegrationsObject(initialIntegrationObject);
    setHasUpdatedEnable(false);
    setErrors(initialErrors);
  };

  useEffect(() => {
    resetAllValues();
  }, [user?.selectedCompany]);

  const handleSubmit = async (skipConfirm?: boolean) => {
    if (
      !skipConfirm &&
      hasUpdatedEnable &&
      !integrationsObject[INTEGRATION_FIELD_NAMES.Enabled]
    ) {
      setIsConfirmDeactivateOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      const { selectedCompany, ...cleanedIntegrationsObject } =
        integrationsObject;

      const payloadObject = {
        ...cleanedIntegrationsObject,
        companyId: integrationsObject.selectedCompany?.id,
        driverFolderNaming: integrationsObject.driverFolderNaming?.value,
        vehicleFolderNaming: integrationsObject.vehicleFolderNaming?.value,
        fileFormat: integrationsObject.fileFormat?.value
      };

      const { data } = await postData(
        ApiResources.IntegrationsSftp,
        payloadObject
      );

      const successMessage = t("Alert##add integration success");

      const errorCodeExists = data?.connectionErrorCode
        ? undefined
        : successMessage;

      const searchParams = integrationsParams.contains;

      getTableItemDetails({
        itemId: data.id,
        successMessage: errorCodeExists,
        leaveTableDetailsNotUpdated: true,
        valueInSearchField: searchParams ?? undefined
      });

      if (data.connectionErrorCode) {
        showError(
          data.connectionErrorCode,
          data,
          IntegrationsErrorDialogSteps.Add
        );
      } else {
        onClose();
        resetAllValues();
      }
    } catch (error: any) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  const hasErrors = Object.values(errors).some(error => error !== "");
  const optionalFields = new Set([
    INTEGRATION_FIELD_NAMES.RemotePath,
    INTEGRATION_FIELD_NAMES.IncludeSubCompanies,
    INTEGRATION_FIELD_NAMES.SyncHistoricalFiles
  ]);

  const requiredFieldsFilled = Object.entries(integrationsObject).every(
    ([key, value]) => {
      if (optionalFields.has(key as INTEGRATION_FIELD_NAMES)) return true;
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    }
  );

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled;

  const handleConfirmSubmit = () => {
    handleSubmit(true);
    setIsConfirmDeactivateOpen(false);
  };

  return (
    <>
      <Dialog
        data-testid="add-integration-dialog"
        title={t("Dialog##sftp integration##title")}
        description={t("Dialog##sftp integration##description")}
        extendToMaxWidth={toMd}
        isTitleSeparator
        isActionsSeparator
        open={isOpen}
        close={isLoading ? noop : onClose}
        submit={handleSubmit}
        width={toMd ? undefined : DialogWidth.Dialog920px}
        slotProps={{ transition: { onExited: resetAllValues } }}
        customPaperMargin="48px"
        actions={
          <DialogActions
            onClose={goBackToTypeSelect}
            disabledClose={isLoading}
            isLoading={isLoading}
            loaderTitle={t("Button##testing connection")}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##save")}
            confirmationButtonTestId="save-integration-button"
            cancelButtonText={t("Button##back")}
            cancelButtonTestId="back-integration-button"
          />
        }
      >
        <IntegrationDialogContent
          isLoading={isLoading}
          integrationsObject={integrationsObject}
          errors={errors}
          hasUpdatedEnable={hasUpdatedEnable}
          setHasUpdatedEnable={setHasUpdatedEnable}
          setErrors={setErrors}
          setIntegrationsObject={setIntegrationsObject}
        />
      </Dialog>

      <ConfirmDeactivateResource
        isOpen={isConfirmDeactivateOpen}
        route={ROUTES.Integrations}
        resourceName={integrationsObject[INTEGRATION_FIELD_NAMES.Name]}
        onClose={() => setIsConfirmDeactivateOpen(false)}
        onSubmit={handleConfirmSubmit}
      />
    </>
  );
};

export default AddIntegrationDialog;
